/* Reports preview — investor PDF mirror.
 *
 * Phase 44 Batch BF2 P0 — branches on `deal_type === "flip"`. For
 * flip deals the lead Financial Summary block becomes a "Flip
 * Performance" table (ARV / gross profit / ROI / hold / ARV ratio /
 * breakeven ARV). The rental fields (monthly rent, cap rate, COC,
 * DSCR, debt service, CF) move into a COLLAPSED "Exit Scenario:
 * Long-term Rental" subsection at the bottom of the Financial
 * Summary — so the rental thesis is preserved for reference but
 * doesn't lead the report.
 *
 * Pre-fix: this component had no deal_type check and always
 * rendered the rental table as the lead, even on flip deals where
 * cap_rate=None / cash_on_cash=None / dscr=None / etc were
 * silently displayed as "—". The PDF backend branched correctly
 * but the React preview didn't → the user saw rental garbage in
 * the web UI.
 */
import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Hammer, ShieldCheck, ShieldAlert, Shield } from "lucide-react";
import VerdictBadge from "@/components/VerdictBadge";
import { formatDscr, propertyTypeLabel } from "@/lib/format";
import { useFmt } from "@/lib/useFmt";
import { strings } from "@/i18n/strings";
// Phase 47.2 — Price-per-SqFt + Price-per-SqM formatter for the
// Property Summary table and the Financial Summary rows.
import { formatPsfPsm } from "@/lib/priceNormalisation";

const S = strings.reports.preview;
const BRAND = strings.brand;

function SectionTitle({ children, sub }) {
  return (
    <div className="mb-3">
      <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">{children}</h3>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function Row({ k, v, tone }) {
  const toneCls = tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-red-600" : "text-slate-900";
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0">
      <dt className="text-sm text-slate-500">{k}</dt>
      <dd className={`text-sm font-medium tabular-nums ${toneCls}`}>{v}</dd>
    </div>
  );
}

function PageBreakHr() {
  return <div className="my-10 border-t border-dashed border-slate-200" />;
}

function SevDot({ severity }) {
  const map = { low: "bg-emerald-500", medium: "bg-amber-500", high: "bg-red-500" };
  return <span className={`mr-2 inline-block h-2 w-2 rounded-full ${map[severity] || "bg-slate-400"}`} />;
}

function ArvSourcePill({ source }) {
  const v = String(source || "unverified").toLowerCase();
  const conf = {
    verified:   { label: "Verified",   bg: "bg-emerald-50", text: "text-emerald-800", ring: "ring-emerald-200", Icon: ShieldCheck },
    estimated:  { label: "Estimated",  bg: "bg-amber-50",   text: "text-amber-800",   ring: "ring-amber-200",   Icon: Shield },
    unverified: { label: "Unverified", bg: "bg-rose-50",    text: "text-rose-800",    ring: "ring-rose-200",    Icon: ShieldAlert },
  }[v] || { label: "Unverified", bg: "bg-rose-50", text: "text-rose-800", ring: "ring-rose-200", Icon: ShieldAlert };
  const Icon = conf.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${conf.bg} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ${conf.ring} ${conf.text}`}>
      <Icon className="h-3 w-3" /> {conf.label}
    </span>
  );
}

function AvmDiscrepancyBanner({ report, fmt }) {
  // Read both shapes — nested `avm_discrepancy` (gap-3b) and the
  // flat fields fall back. ``flip_performance`` carries the
  // engine's view, ``report.avm_discrepancy`` may also be present
  // on rentals/BRRRRs.
  const fp = report.flip_performance || {};
  const nested = report.avm_discrepancy || null;
  const warning = !!(nested?.warning ?? fp.avm_discrepancy_warning);
  if (!warning) return null;
  const severity = nested?.severity || fp.avm_discrepancy_severity || "medium";
  const deltaPct = (nested?.delta_pct ?? fp.asking_vs_avm_pct_below ?? 0) * 100;
  const avm = nested?.avm ?? fp.avm_price;
  const asking = nested?.asking ?? report.financial_summary?.purchase_price;
  const isHigh = severity === "high";
  return (
    <div
      data-testid="report-avm-discrepancy-banner"
      className={`mb-6 flex items-start gap-3 rounded-xl border p-4 shadow-sm ${
        isHigh ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"
      }`}
      role="alert"
    >
      <AlertTriangle className={`mt-0.5 h-5 w-5 flex-shrink-0 ${isHigh ? "text-red-700" : "text-amber-700"}`} />
      <div className="min-w-0 text-sm">
        <p className={`font-semibold ${isHigh ? "text-red-900" : "text-amber-900"}`}>
          {isHigh ? "AVM discrepancy (HIGH) — likely foreclosure or title issue" : "AVM discrepancy — verify the listing"}
        </p>
        <p className={`mt-1 ${isHigh ? "text-red-800" : "text-amber-800"}`}>
          Asking <span className="font-medium tabular-nums">{fmt.money(asking)}</span> vs
          AVM <span className="font-medium tabular-nums">{fmt.money(avm)}</span> — asking is{" "}
          <span className="font-semibold">{deltaPct.toFixed(0)}% below AVM</span>.{" "}
          {isHigh
            ? "Pull title and recent foreclosure comps before underwriting."
            : "Verify with a broker call + recent comps before underwriting."}
        </p>
      </div>
    </div>
  );
}

function FlipPerformanceTable({ fp, fin, fmt }) {
  const gp = fp.gross_profit || 0;
  const profitTone = gp >= 0 ? "good" : "bad";
  const arvPct = fp.deal_to_arv_pct;
  const arvTone = arvPct != null && arvPct <= 70 ? "good" : arvPct != null && arvPct <= 80 ? null : "bad";
  return (
    <dl data-testid="report-section-flip-performance">
      <Row k="Purchase price" v={fmt.money(fin.purchase_price)} />
      <Row
        k="ARV (after-repair value)"
        v={
          <span className="inline-flex items-center gap-2">
            <span className="tabular-nums">{fmt.money(fp.arv)}</span>
            <ArvSourcePill source={fp.arv_source} />
          </span>
        }
      />
      <Row k="Repair / rehab budget" v={fmt.money(fp.repair_budget)} />
      <Row k="Hold period (months)" v={fp.holding_period_months ?? "—"} />
      <Row k="Total holding cost" v={fmt.money(fp.holding_costs_total)} />
      <Row k="Interest during hold" v={fmt.money(fp.interest_during_hold)} />
      <Row k="Selling costs" v={fmt.money(fp.selling_costs, { cents: true })} />
      <Row k="Total project cost" v={fmt.money(fp.total_project_cost)} />
      <Row k="Total cash invested" v={fmt.money(fp.total_cash_invested)} />
      <Row k="Net sale proceeds" v={fmt.money(fp.net_sale_proceeds)} />
      <Row k="Gross profit" v={fmt.money(gp, { cents: true })} tone={profitTone} />
      <Row
        k="Gross margin"
        v={fp.gross_margin != null ? `${(Number(fp.gross_margin) * 100).toFixed(2)}%` : "—"}
      />
      <Row
        k="Flip ROI (annualised)"
        v={fp.annualized_roi_pct != null ? `${Number(fp.annualized_roi_pct).toFixed(2)}%` : "—"}
        tone="good"
      />
      <Row k="ROI on cash invested" v={fp.roi_pct != null ? `${Number(fp.roi_pct).toFixed(2)}%` : "—"} />
      <Row
        k="ARV ratio (70% rule anchor)"
        v={arvPct != null ? `${Number(arvPct).toFixed(2)}%` : "—"}
        tone={arvTone}
      />
      <Row k="Breakeven ARV" v={fmt.money(fp.breakeven_arv)} />
      <Row k="Deal score" v={`${fin.deal_score ?? "—"} / 100`} />
      <Row k="Verdict" v={fin.verdict || "—"} />
    </dl>
  );
}

function RentalFinancialTable({ fin, fmt, cfTone }) {
  return (
    <dl>
      <Row k="Purchase price" v={fmt.money(fin.purchase_price)} />
      <Row k="Monthly rent" v={fmt.money(fin.monthly_rent)} />
      <Row k="NOI (monthly)" v={fmt.money(fin.noi_monthly, { cents: true })} />
      <Row k="NOI (annual)" v={fmt.money(fin.noi_annual, { cents: true })} />
      <Row k="Cap rate" v={fmt.percent(fin.cap_rate)} />
      <Row k="Cash-on-cash" v={fmt.percent(fin.cash_on_cash)} />
      <Row k="DSCR" v={formatDscr(fin.dscr)} />
      <Row k="Monthly debt service" v={fmt.money(fin.monthly_debt_service, { cents: true })} />
      <Row k="Cash flow (monthly)" v={fmt.money(fin.cash_flow_monthly, { cents: true })} tone={cfTone} />
      <Row k="Cash flow (annual)" v={fmt.money(fin.cash_flow_annual, { cents: true })} tone={cfTone} />
      <Row k="Total cash invested" v={fmt.money(fin.total_cash_invested)} />
      <Row k="Loan amount" v={fmt.money(fin.loan_amount)} />
      <Row k="Down payment" v={fmt.money(fin.down_payment_amount)} />
    </dl>
  );
}

function ExitScenarioRentalCollapsible({ fin, fmt }) {
  const [open, setOpen] = useState(false);
  const cfTone = fin.cash_flow_monthly >= 0 ? "good" : "bad";
  return (
    <div
      data-testid="report-section-exit-scenario"
      className="mt-6 rounded-xl border border-slate-200 bg-slate-50"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
        data-testid="report-exit-scenario-toggle"
      >
        <span className="text-sm font-semibold text-slate-800">
          {open
            ? <ChevronDown className="inline h-4 w-4 text-slate-500 align-text-bottom" />
            : <ChevronRight className="inline h-4 w-4 text-slate-500 align-text-bottom" />}
          {" "}Exit Scenario: Long-term Rental (backup)
        </span>
        <span className="text-xs italic text-slate-500">
          What it looks like if you held it instead of flipping
        </span>
      </button>
      {open ? (
        <div className="border-t border-slate-200 px-4 pb-4 pt-3 bg-white rounded-b-xl">
          <RentalFinancialTable fin={fin} fmt={fmt} cfTone={cfTone} />
          <p className="mt-3 text-[11px] italic text-slate-500">
            Rough rental underwrite for reference only — the lead thesis on this deal is a flip.
          </p>
        </div>
      ) : null}
    </div>
  );
}

// Phase 47.1 — BRRRR Performance section. Mirrors FlipPerformanceTable's
// shape (KV rows + highlight tones + "Infinite Return" badge). Renders
// the 11 BRRRR KPIs the analyst needs to read at a glance before
// drilling into the Tri-View on Deal Detail. The companion
// `BrrrrExitScenarioCollapsible` below relegates the pre-refi rental
// values to a bottom collapsible (parallel to flip's pattern).
function BrrrrPerformanceTable({ bp, fin, fmt }) {
  const capRecycled = Number(bp.capital_recycled || 0);
  const capPct = Number(bp.capital_recycled_pct || 0);
  const cashLeft = Number(bp.cash_left_in_deal || 0);
  const equityCreated = Number(bp.equity_created || 0);
  return (
    <div data-testid="brrrr-performance-table">
      {bp.infinite_return ? (
        <div
          data-testid="brrrr-infinite-return-badge"
          className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3"
        >
          <span className="text-lg">♾</span>
          <div className="text-sm">
            <span className="font-semibold text-emerald-900">Infinite Return</span>
            <span className="text-emerald-700"> — all cash invested is recycled via the refi. Every dollar of ongoing cash flow is pure yield on zero remaining basis.</span>
          </div>
        </div>
      ) : null}
      <dl>
        <Row k="Purchase price" v={fmt.money(fin.purchase_price)} />
        <Row k="ARV (after-repair value)" v={fmt.money(bp.arv)} />
        <Row k="ARV source" v={(bp.arv_source || "unverified").replace(/^./, (c) => c.toUpperCase())} />
        <Row k="Repair / rehab budget" v={fmt.money(bp.repair_budget)} />
        <Row k="Total cash invested" v={fmt.money(bp.total_cash_invested)} />
        <Row k="Refi period (months)" v={bp.refi_period_months ?? "—"} />
        <Row k="Refi LTV" v={`${Number(bp.refi_ltv_pct || 0).toFixed(0)}%`} />
        <Row k="Refi rate" v={`${Number(bp.refi_interest_rate || 0).toFixed(2)}%`} />
        <Row k="Refi term (years)" v={bp.refi_loan_term_years ?? "—"} />
        <Row k="Refi loan amount" v={fmt.money(bp.refi_loan_amount)} />
        <Row k="Refi closing costs" v={fmt.money(bp.refi_closing_costs)} />
        <Row
          k="Capital Recycled"
          v={`${fmt.money(capRecycled)} (${capPct.toFixed(0)}% of cash invested)`}
          tone={capPct >= 100 ? "good" : null}
        />
        <Row
          k="Equity Created"
          v={fmt.money(equityCreated)}
          tone={equityCreated >= 0 ? "good" : "bad"}
        />
        <Row
          k="Cash Left in Deal"
          v={fmt.money(cashLeft)}
          tone={cashLeft === 0 ? "good" : null}
        />
        <Row k="Post-Refi Cap Rate" v={bp.post_refi_cap_rate != null ? fmt.percent(bp.post_refi_cap_rate) : "—"} />
        <Row k="Post-Refi DSCR" v={bp.post_refi_dscr != null ? `${Number(bp.post_refi_dscr).toFixed(2)}x` : "—"} />
        <Row k="Post-Refi Cash Flow (monthly)" v={fmt.money(bp.post_refi_cash_flow_monthly, { cents: true })} />
        <Row k="Post-Refi Cash Flow (annual)" v={fmt.money(bp.post_refi_cash_flow_annual, { cents: true })} />
        <Row k="Deal score" v={`${fin.deal_score ?? "—"} / 100`} />
        <Row k="Verdict" v={fin.verdict || "—"} />
      </dl>
    </div>
  );
}

// Phase 47.1 — BRRRR "Pure Rental (no refinance)" collapsible. Like
// the flip Exit Scenario, gated to non-trivial rental values so we
// don't show a row of dashes on a pure-cash-purchase BRRRR.
function BrrrrPureRentalCollapsible({ fin, fmt }) {
  const [open, setOpen] = useState(false);
  const rent = Number(fin.monthly_rent || 0);
  if (rent <= 0) return null;
  return (
    <div
      data-testid="report-section-brrrr-exit-rental"
      className="mt-6 rounded-xl border border-slate-200 bg-slate-50"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
        data-testid="report-brrrr-exit-rental-toggle"
      >
        <span className="text-sm font-semibold text-slate-800">
          {open
            ? <ChevronDown className="inline h-4 w-4 text-slate-500 align-text-bottom" />
            : <ChevronRight className="inline h-4 w-4 text-slate-500 align-text-bottom" />}
          {" "}Exit Scenario: Pure Rental (no refinance)
        </span>
        <span className="text-xs italic text-slate-500">
          What it looks like if the cash stays in the deal
        </span>
      </button>
      {open ? (
        <div className="border-t border-slate-200 px-4 pb-4 pt-3 bg-white rounded-b-xl">
          <dl>
            <Row k="Monthly rent" v={fmt.money(fin.monthly_rent)} />
            <Row k="Original list price" v={fmt.money(fin.purchase_price)} />
          </dl>
          <p className="mt-3 text-[11px] italic text-slate-500">
            Pre-refi rental economics depend on the financing assumption.
            See the Deal Detail page for the no-refi rental scenario in full.
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default function ReportPreview({ report }) {
  const fmt = useFmt();
  if (!report) return null;
  const prop = report.property || {};
  const fin = report.financial_summary || {};
  const fa = report.financing_assumptions || {};
  const ai = report.ai_recommendation;
  const risk = report.risk_analysis;
  const neg = report.offer_strategy;
  const cfTone = fin.cash_flow_monthly >= 0 ? "good" : "bad";
  // Phase 44 Batch BF2 P0 — flip branch.
  const isFlip = (report.deal_type || "").toLowerCase() === "flip";
  const fp = report.flip_performance || {};
  // Phase 47.1 — BRRRR branch (mirrors flip's pattern).
  const isBrrrr = (report.deal_type || "").toLowerCase() === "brrrr";
  const bp = report.brrrr_performance || {};
  // Phase 44 Batch BF2 P0 — cash flip → hide the financing card
  // (no loan, no debt service, no interest rate). Renders a cleaner
  // "Cash purchase — no financing" tag in place. A leveraged flip
  // still shows the financing block.
  const isCashPurchase = isFlip && (!fin.loan_amount || Number(fin.loan_amount) === 0);

  return (
    <div data-testid="report-preview-root" className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10">
      {/* Cover */}
      <header className="text-center">
        <img src="/logo.png" alt="" className="mx-auto mb-4 h-12 w-12 rounded-lg object-cover" />
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600">{BRAND.name}</div>
        <h1 className="mt-6 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{S.title}</h1>
        <p className="mt-2 text-base text-slate-600">{prop.address || "—"}</p>
        <p className="text-sm text-slate-500">
          {[prop.city, prop.state, prop.zip].filter(Boolean).join(", ")}
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-5">
          <VerdictBadge verdict={fin.verdict} size="lg" />
          <div className="text-left">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Deal score</div>
            <div className="text-3xl font-bold tabular-nums text-slate-900">
              {fin.deal_score ?? "—"} <span className="text-base font-medium text-slate-400">/ 100</span>
            </div>
          </div>
        </div>
        <div className="mt-6 text-xs text-slate-500">
          {S.prepared_for}: <span className="font-medium text-slate-700">{report.prepared_for?.name}</span>
          {"  ·  "}
          {S.generated}: {new Date(report.generated_at).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </div>
      </header>

      <PageBreakHr />

      {/* Phase 44 BF2 P0 — AVM discrepancy banner sits ABOVE Property
          Summary so the analyst is forewarned before they read AVM. */}
      <AvmDiscrepancyBanner report={report} fmt={fmt} />

      {/* Property summary */}
      <section data-testid="report-section-property">
        <SectionTitle>{S.property_summary}</SectionTitle>
        <dl>
          <Row k="Address" v={prop.address || "—"} />
          <Row k="Location" v={[prop.city, prop.state, prop.zip].filter(Boolean).join(", ") || "—"} />
          <Row k="Property type" v={propertyTypeLabel(prop.property_type)} />
          <Row k="Units · Beds · Baths · Sqft" v={`${prop.units || 1} · ${prop.bedrooms} · ${prop.bathrooms} · ${prop.square_feet}`} />
          <Row k="Asking price" v={fmt.money(prop.asking_price)} />
          {/* Phase 47.2 — Price-per-SqFt + Price-per-SqM normalisation
              rows. Investor-language polish: $/sqft is the universal US
              comp metric; $/sqm is what LATAM/EU Miami buyers think in.
              Both rendered as one ":" row to keep the Property Summary
              table compact. */}
          <Row
            k="Asking $/sqft · $/sqm"
            v={formatPsfPsm(prop.asking_price, prop.square_feet)}
          />
          {report.counter_price_per_sqft != null ? (
            <Row
              k="Counter $/sqft · $/sqm"
              v={formatPsfPsm(fin.counter_offer_price, prop.square_feet)}
            />
          ) : null}
          <Row
            k="Estimated value"
            v={
              <span className="inline-flex items-center gap-2">
                <span className="tabular-nums">{fmt.money(prop.estimated_value)}</span>
                {prop.estimated_value_source === "avm" ? (
                  <span
                    className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-indigo-700 ring-1 ring-indigo-200"
                    title="Sourced from market-data AVM"
                  >
                    AVM
                  </span>
                ) : null}
              </span>
            }
          />
          <Row
            k="Estimated value $/sqft · $/sqm"
            v={formatPsfPsm(prop.estimated_value, prop.square_feet)}
          />
        </dl>
      </section>

      <PageBreakHr />

      {/* Financial summary — brrrr vs flip vs rental */}
      <section data-testid="report-section-financial">
        {isBrrrr ? (
          <>
            <SectionTitle sub="Lead underwriting: BRRRR (Buy / Rehab / Rent / Refi / Repeat)">
              <span className="inline-flex items-center gap-1.5">
                ♾ BRRRR Performance
              </span>
            </SectionTitle>
            <BrrrrPerformanceTable bp={bp} fin={fin} fmt={fmt} />
            <BrrrrPureRentalCollapsible fin={fin} fmt={fmt} />
          </>
        ) : isFlip ? (
          <>
            <SectionTitle sub="Lead underwriting: house flip">
              <span className="inline-flex items-center gap-1.5">
                <Hammer className="h-3 w-3" /> Flip Performance
              </span>
            </SectionTitle>
            <FlipPerformanceTable fp={fp} fin={fin} fmt={fmt} />
            <ExitScenarioRentalCollapsible fin={fin} fmt={fmt} />
          </>
        ) : (
          <>
            <SectionTitle>{S.financial_summary}</SectionTitle>
            <RentalFinancialTable fin={fin} fmt={fmt} cfTone={cfTone} />
          </>
        )}
      </section>

      {risk && (
        <>
          <PageBreakHr />
          <section data-testid="report-section-risk">
            <SectionTitle sub={`${S.overall_risk}: ${risk.overall_risk}`}>{S.risk_analysis}</SectionTitle>
            {risk.overall_summary && <p className="mb-4 text-sm leading-relaxed text-slate-700">{risk.overall_summary}</p>}
            <ul className="space-y-3">
              {(risk.categories || []).map((c) => (
                <li key={c.key} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center text-sm font-semibold text-slate-900">
                      <SevDot severity={c.severity} />
                      {c.label}
                    </div>
                    {c.data_source === "estimated_placeholder" && (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">
                        {S.estimated_label}
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{c.summary}</p>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      {ai && (
        <>
          <PageBreakHr />
          <section data-testid="report-section-ai">
            <SectionTitle>{S.ai_recommendation}</SectionTitle>
            <VerdictBadge verdict={ai.verdict} size="lg" />
            <h2 className="mt-3 text-lg font-semibold tracking-tight text-slate-900">{ai.headline}</h2>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">{ai.main_reason}</p>

            {ai.top_risks?.length > 0 && (
              <div className="mt-5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{S.top_risks}</h4>
                <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                  {ai.top_risks.map((r, i) => (
                    <li key={`risk-${i}-${r.title}`} className="flex">
                      <SevDot severity={r.severity} />
                      <span><b>{r.title}</b> — {r.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ai.top_opportunities?.length > 0 && (
              <div className="mt-5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{S.top_opportunities}</h4>
                <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                  {ai.top_opportunities.map((o, i) => (
                    <li key={`opp-${i}-${o.title}`} className="flex">
                      <span className="mr-2 inline-block h-2 w-2 rounded-full bg-emerald-500" />
                      <span><b>{o.title}</b> — {o.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ai.investor_grade_conclusion && (
              <p className="mt-6 rounded-lg bg-slate-50 p-4 text-sm leading-relaxed text-slate-800">
                {ai.investor_grade_conclusion}
              </p>
            )}
          </section>
        </>
      )}

      {neg && (
        <>
          <PageBreakHr />
          <section data-testid="report-section-offers">
            <SectionTitle sub={`Asking: ${fmt.money(neg.asking_price)}`}>{S.offer_strategy}</SectionTitle>
            <div className="grid gap-3 sm:grid-cols-3">
              {["conservative_offer", "recommended_offer", "max_offer"].map((k) => {
                const o = neg.offers?.[k];
                if (!o) return null;
                const label = { conservative_offer: "Conservative", recommended_offer: "Recommended", max_offer: "Max" }[k];
                const highlight = k === "recommended_offer";
                return (
                  <div
                    key={k}
                    className={`rounded-lg border p-3 ${
                      highlight ? "border-indigo-300 bg-indigo-50/50 ring-2 ring-indigo-100" : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
                    <div className="mt-1 text-lg font-bold text-slate-900 tabular-nums">{fmt.money(o.price)}</div>
                    <div className="text-xs text-slate-500">{o.delta_pct_from_ask?.toFixed(1)}% vs ask</div>
                    <p className="mt-2 text-xs leading-relaxed text-slate-600">{o.justification}</p>
                  </div>
                );
              })}
            </div>

            {neg.broker_questions?.length > 0 && (
              <div className="mt-5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{S.broker_questions}</h4>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-700">
                  {neg.broker_questions.map((q, i) => <li key={`bq-${i}-${q.slice(0,20)}`}>{q}</li>)}
                </ol>
              </div>
            )}
            {neg.seller_motivation_clues?.length > 0 && (
              <div className="mt-5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{S.seller_motivation_clues}</h4>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
                  {neg.seller_motivation_clues.map((c, i) => <li key={`smc-${i}-${c.slice(0,20)}`}>{c}</li>)}
                </ul>
              </div>
            )}
            {neg.realtor_email_template && (
              <div className="mt-5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{S.realtor_email}</h4>
                <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-50 p-4 font-mono text-xs leading-relaxed text-slate-800">
{neg.realtor_email_template}
                </pre>
              </div>
            )}
          </section>
        </>
      )}

      <PageBreakHr />

      {/* Phase 44 BF2 P0 — financing block. Cash flip → replace the
          ghost-row financing table with a "Cash purchase" notice.
          Leveraged flips and rentals fall through to the standard
          table. */}
      <section data-testid="report-section-financing">
        <SectionTitle>{S.financing_assumptions}</SectionTitle>
        {isCashPurchase ? (
          <div
            data-testid="report-financing-cash-purchase"
            className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm"
          >
            <p className="font-semibold text-slate-800">Cash purchase — no financing.</p>
            <p className="mt-1 text-slate-600">
              No loan, no debt service, no interest. Closing costs:{" "}
              <span className="font-medium tabular-nums">{fmt.money(fa.closing_costs)}</span>.
            </p>
          </div>
        ) : (
          <dl>
            <Row k="Down payment" v={`${fa.down_payment_percent}%`} />
            <Row k="Interest rate" v={`${fa.interest_rate}%`} />
            <Row k="Loan term" v={`${fa.loan_term_years} years`} />
            <Row k="Closing costs" v={fmt.money(fa.closing_costs)} />
            <Row k="Loan amount" v={fmt.money(fin.loan_amount)} />
            <Row k="Monthly debt service" v={fmt.money(fin.monthly_debt_service, { cents: true })} />
          </dl>
        )}
      </section>

      <footer className="mt-10 border-t border-slate-200 pt-6 text-center text-xs text-slate-400">
        {BRAND.legal_footer}
      </footer>
    </div>
  );
}
